// @flow
import type { Sample, DataField, TestSchedule, Product, CompanyBranch } from "../../lib/Types";
import React, { Fragment } from "react";
import ShoppingCart from "./ShoppingCart";
import SampleEditor from "./SampleEditor";
import BasketData from "../../lib/BasketData";
import { post, postMultipart } from "../../lib/Fetching";
import styled from "styled-components";
import FixedSampleList from "./FixedSampleList";
import ShoppingLists from "./ShoppingLists";
import Button from "../Button";
import Basket from "./Basket";
import SampleStore from "../../lib/SampleStore";
import RegistrationModal from "./RegistrationModal";
import CompanyBranchDropDown from "../crm/CompanyBranchDropDown";
import RegistrationFlow from "./RegistrationFlow";
import AddToShoppingList from "./AddToShoppingList";
import Authoriser from "../../lib/Authoriser";
import NewSampleFromStandaloneQuotes from "./NewSampleFromStandaloneQuotes";
import AsyncResponse from "../common/AsyncResponse";
import ExcelUploadForm from "./ExcelUploadForm";
import ErrorMessages from "../common/ErrorMessages"; // Add this import

type Props = {
  company_id: number,
  clientView: boolean,
  client_permissions: ?Array<string>,
};

type State = {
  mode: string,
  samples: Array<Sample>,
  registeringAtBranch: ?CompanyBranch,
  showModal: boolean,
  excelFile: ?File,
};

const HeaderText = styled.h4`
  font-size: 16pt;
  margin-top: 0px;
`;

export default class SampleRegistration extends React.Component<Props, State> {
  state = {
    authoriser: new Authoriser(this.props.authoriser),
    mode: "shopping",
    tab: "basket",
    registeringAtBranch: null,
    samples: [],
    viewSampleIds: [],
    shoppingListGeneration: 1,
    sampleStore: new SampleStore([], "sample_information", this.props.company_id),
    showModal: false,
    addingToShoppingList: false,
    asyncResponse: null,
    excelFile: null,
    excelUploadVisible: false,
    excelUploadErrors: [],
  };

  constructor(props: Props) {
    super(props);
    this.basketData = new BasketData(props.company_id);
    this.fileInputRef = React.createRef();
    this.authoriser = new Authoriser(this.props.authoriser);
  }

  canUploadExcel() {
    return this.authoriser.has_client_permission("excel_registration");
  }

  updateRegisteringBranch = (registeringAtBranch: CompanyBranch) => {
    const { sampleStore } = this.state;
    if (this.state.registeringAtBranch !== null && sampleStore.samples.length > 0) {
      console.error("eek");
      alert(
        "You cannot change the branch when there are existing samples in your basket. This ensures you cannot register samples against a branch they aren't allowed at."
      );
    } else {
      this.setState({ registeringAtBranch });
    }
  };

  // User clicked on 'Begin Registering'

  addToRegistrationList = (basketItems, standaloneQuotes = false) => {
    const { company_id } = this.props;
    const { registeringAtBranch, samples, sampleStore } = this.state;
    if (!registeringAtBranch) return null;
    this.setState({
      tab: "registering",
    });
    post("/registration/samples/new", {
      company_id,
      company_branch_id: registeringAtBranch.id,
      data: basketItems,
    }).then((json) => {
      json.samples.forEach((s) => {
        s.selected = true;
        sampleStore.samples.push(s);
      });
      this.setState({
        samples: samples.concat(json.samples),
        sampleStore: sampleStore,
      });
    });
  };

  addProtocolToRegistrationList = ({ product, protocol_id }) => {
    const { company_id } = this.props;
    const { registeringAtBranch, samples, sampleStore } = this.state;
    if (!registeringAtBranch) return null;
    this.setState({
      tab: "registering",
    });
    post("/registration/samples/new_testing_protocol", {
      company_id,
      company_branch_id: registeringAtBranch.id,
      protocol_id,
      product,
    }).then((json) => {
      json.samples.forEach((s) => {
        s.selected = true;
        sampleStore.samples.push(s);
      });
      this.setState({
        samples: samples.concat(json.samples),
        sampleStore: sampleStore,
      });
    });
  };

  // User clicked on 'Register Samples' after adding sample data
  createSamples = (samples: Array<Sample>) => {
    const { company_id } = this.props;
    const { registeringAtBranch } = this.state;
    if (!registeringAtBranch) {
      alert("You must select a branch");
      return Promise.resolve();
    }
    return post("/registration/samples/create_async", {
      company_id,
      company_branch_id: registeringAtBranch.id,
      samples,
    })
      .then((asyncResponse) => {
        this.setState({ asyncResponse });
      })
      .catch((msg) => {
        this.cancelSampleSubmission();
        alert(msg);
      });
  };

  afterSamplesSubmitted = (json) => {
    const { sampleStore } = this.state;
    sampleStore.clearStorage();
    this.setState({ viewSampleIds: json.result.sample_ids, samples: [], asyncResponse: null });
  };

  cancelSampleSubmission = () => {
    this.setState({ asyncResponse: null });
  };

  incrementShoppingListGeneration = () => {
    this.setState({
      shoppingListGeneration: this.state.shoppingListGeneration + 1,
      mode: "shopping_lists",
    });
  };

  registerSamplesFromExcel = (excelFile, ignoreErrors) => {
    if (!excelFile) return Promise.reject("No file selected");

    console.log("registerSamplesFromExcel", excelFile, ignoreErrors);

    const formData = new FormData();
    formData.append("file", excelFile);
    formData.append("company_id", this.props.company_id);
    formData.append("ignore_errors", ignoreErrors);

    this.setState({ excelUploadErrors: [] });

    return postMultipart("/registration/samples/new_from_excel_registration", {
      file: excelFile,
      company_id: this.props.company_id,
      ignore_errors: ignoreErrors,
    })
      .then((json) => {
        if (json.errors.length > 0) {
          this.setState({ excelUploadErrors: json.errors, excelUploadVisible: false });
          if (ignoreErrors && json.samples && json.samples.length > 0) {
            const { sampleStore } = this.state;
            json.samples.forEach((s) => {
              s.selected = true;
              sampleStore.samples.push(s);
            });
            this.setState({
              samples: [...this.state.samples, ...json.samples],
              sampleStore: sampleStore,
            });
          }
        } else {
          const { sampleStore } = this.state;
          json.samples.forEach((s) => {
            s.selected = true;
            sampleStore.samples.push(s);
          });
          this.setState({
            samples: [...this.state.samples, ...json.samples],
            sampleStore: sampleStore,
            excelUploadVisible: false,
            excelUploadErrors: [],
          });
        }
      })
      .catch((error) => {
        console.error("Error registering samples from Excel:", error);
        this.setState({
          excelUploadErrors: ["An error occurred while registering samples from Excel file."],
          excelUploadVisible: false,
        });
        throw error;
      });
  };

  renderExcelUpload() {
    const { registeringAtBranch } = this.state;
    const { company_id } = this.props;

    if (!registeringAtBranch) {
      return null;
    }
    return (
      <ExcelUploadForm
        registeringAtBranch={registeringAtBranch}
        company_id={company_id}
        onUpload={this.registerSamplesFromExcel}
      />
    );
  }

  renderBasket() {
    const { company_id } = this.props;
    const {
      registeringAtBranch,
      mode,
      samples,
      showModal,
      authoriser,
      excelUploadErrors,
    } = this.state;
    return (
      <div
        style={{
          maxWidth: 1900,
          margin: "0 auto",
          marginTop: 20,
        }}
      >
        <div style={{ marginBottom: 20 }}>
          <Button
            title={I18n.t("products")}
            highlight={mode === "shopping"}
            onClick={() => this.setState({ mode: "shopping" })}
            style={{ marginRight: 20 }}
          />
          <Button
            title={I18n.t("lists")}
            highlight={mode === "shopping_lists"}
            onClick={() => {
              this.setState({ mode: "shopping_lists" });
              this.incrementShoppingListGeneration();
            }}
            style={{ marginRight: 20 }}
          />
          <Button
            title={I18n.t("from_quotes")}
            highlight={mode === "quotes"}
            onClick={() => {
              this.setState({ mode: "quotes" });
            }}
            style={{ marginRight: 20 }}
          />
          <Button
            className="pull-right"
            title={I18n.t("close")}
            icon="fa-close"
            onClick={() => this.setState({ showModal: !showModal })}
          />
        </div>
        {mode === "shopping" &&
          registeringAtBranch && (
            <ShoppingCart
              company_id={company_id}
              registeringBranch={registeringAtBranch}
              onBranchChange={this.updateRegisteringBranch}
              fixedBranch={samples.length > 0}
              pickSchedule={(schedule) => this.addToRegistrationList([schedule])}
              registerProtocol={this.addProtocolToRegistrationList}
              authoriser={authoriser}
            />
          )}
        {mode === "shopping_lists" && (
          <ShoppingLists
            key={registeringAtBranch.id}
            company_id={company_id}
            branch_id={registeringAtBranch.id}
            registerItems={this.addToRegistrationList}
          />
        )}
        {mode === "quotes" && (
          <NewSampleFromStandaloneQuotes
            company_id={company_id}
            branch_id={registeringAtBranch.id}
            addToRegistrationList={this.addToRegistrationList}
            onClose={() => this.setState({ showModal: false })}
          />
        )}
      </div>
    );
  }

  registerSamples() {
    const {
      registeringAtBranch,
      sampleStore,
      showModal,
      addingToShoppingList,
      asyncResponse,
      excelUploadVisible,
      excelUploadErrors,
    } = this.state;
    const { company_id } = this.props;
    return (
      <div style={{ display: "table", margin: "0 auto", paddingLeft: 10, minWidth: "85vw" }}>
        {asyncResponse && (
          <AsyncResponse
            asyncResponse={asyncResponse}
            onComplete={this.afterSamplesSubmitted}
            onError={this.cancelSampleSubmission}
          />
        )}

        <RegistrationModal
          show={showModal}
          onClose={() => this.setState({ showModal: false })}
        >
          {this.renderBasket()}
        </RegistrationModal>
        <div>
          <div style={{ maxWidth: "95vw" }}>
            <HeaderText style={{ width: 500 }}>
              <CompanyBranchDropDown
                company_id={company_id}
                onBranchChange={this.updateRegisteringBranch}
                value={registeringAtBranch ? registeringAtBranch.id : null}
                withProducts={true}
                blankLabel={"Select branch to register at"}
                permission_code={"register_samples"}
              />
            </HeaderText>
          </div>
          <div>
            <SampleEditor
              sampleStore={sampleStore}
              handleSubmit={this.createSamples}
              buttonTitle={I18n.t("register_samples")}
              showReason={false}
              company_id={company_id}
              isSubmitting={asyncResponse != null}
              showPlaceholder
            >
              {registeringAtBranch && (
                <Button
                  icon="fa-plus"
                  title="Add Samples"
                  style={{ marginRight: 20 }}
                  onClick={() => this.setState({ showModal: !showModal })}
                />
              )}

              {sampleStore.samples.length > 0 &&
                sampleStore.allSamplesAreFromClientList() && (
                  <Button
                    onClick={() =>
                      this.setState({ addingToShoppingList: !addingToShoppingList })
                    }
                    icon="fa-plus"
                    title="Add to List"
                    style={{ marginRight: 20 }}
                  />
                )}

              {sampleStore.samples.length > 0 && (
                <Button
                  icon="fa-trash"
                  title="Clear All"
                  style={{ marginRight: 20 }}
                  onClick={() => {
                    if (confirm("Are you sure you want to clear these samples?")) {
                      sampleStore.clearStorage();
                      this.setState({ samples: [] });
                    }
                  }}
                />
              )}

              {registeringAtBranch &&
                this.canUploadExcel() && (
                  <Button
                    icon="fa-upload"
                    title="Excel Upload"
                    style={{ marginRight: 20 }}
                    onClick={() => this.setState({ excelUploadVisible: !excelUploadVisible })}
                  />
                )}

              {addingToShoppingList && (
                <AddToShoppingList
                  company_id={company_id}
                  onClose={() => this.setState({ addingToShoppingList: false })}
                  onChange={() => {}}
                  basketItems={sampleStore.samples.map((s) => ({
                    product: s.product,
                    test_schedule: s.schedule,
                    count: 1,
                  }))}
                />
              )}
            </SampleEditor>
            {this.state.excelUploadVisible &&
              registeringAtBranch &&
              this.canUploadExcel() &&
              this.renderExcelUpload()}
            {sampleStore.samples.length > 0 &&
              !this.state.excelUploadVisible &&
              excelUploadErrors.length === 0 && (
                <p style={{ color: "#AAA", marginTop: 20 }}>
                  {I18n.t("hint_click_edit")}
                  <br />
                  {I18n.t("hints_for_quick_keyboard_entry")}
                  <ul>
                    <li>
                      {I18n.t("hint_press_ctrl_d_to_copy_down_the_currently_selected_field")}
                    </li>
                    <li>{I18n.t("hint_press_tab")}</li>
                    <li>{I18n.t("hint_date_select")}</li>
                    <li>{I18n.t("hint_date_select_year")}</li>
                    <li>{I18n.t("hint_arrow_navigation")}</li>
                  </ul>
                </p>
              )}
          </div>
        </div>
        {excelUploadErrors.length > 0 && <ErrorMessages errors={excelUploadErrors} />}
      </div>
    );
  }

  viewSamples() {
    const { company_id, clientView } = this.props;
    const { viewSampleIds } = this.state;
    return (
      <Fragment>
        <HeaderText>{I18n.t("viewing_registered_samples")}</HeaderText>
        <Button
          title="Register more samples"
          onClick={() => this.setState({ viewSampleIds: [] })}
        />
        <FixedSampleList
          company_id={company_id}
          sampleIds={viewSampleIds}
          clientView={clientView}
          showAttributes={false}
        />
      </Fragment>
    );
  }

  render() {
    const { viewSampleIds } = this.state;
    if (viewSampleIds.length > 0) {
      return this.viewSamples();
    }
    return <Fragment>{this.registerSamples()}</Fragment>;
  }
}
